/**
 * Content API Contracts
 *
 * Content management endpoints (posts, announcements, events).
 */

import { z } from 'zod'
import { ContentTargetSchema } from '../../schemas/entities/content-target'
import {
  ContentRecordSchema,
  ContentTypeSchema,
  ContentStatusSchema,
  ContentCategorySchema,
  NotificationModeSchema,
  PlatformNewsItemSchema,
  PlatformNewsCategorySchema,
  PlatformMediaItemSchema
} from '../../schemas/entities/content'
import { PollCreateSchema } from '../../schemas/entities/poll'
import { isHttpUrl } from '../../schemas/entities/home-card'

// Re-export core content types
export { ContentRecordSchema, ContentTypeSchema, ContentStatusSchema, ContentCategorySchema, NotificationModeSchema }
export type {
  ContentRecord,
  ContentType,
  ContentStatus,
  ContentCategory,
  NotificationMode
} from '../../schemas/entities/content'

// Re-export Platform News types (for /v1/schools/:schoolId/platform-news)
export { PlatformNewsItemSchema, PlatformNewsCategorySchema, PlatformMediaItemSchema }
export type {
  PlatformNewsItem,
  PlatformNewsCategory,
  PlatformMediaItem,
  PlatformUser,
  ContentRecordRef,
  SchoolRef
} from '../../schemas/entities/content'

// Type aliases for backward compatibility with old web app code
// NewsItem is the Platform News format (rich synced content from Blackbaud)
export type NewsItem = z.infer<typeof PlatformNewsItemSchema>
export type NewsCategory = z.infer<typeof PlatformNewsCategorySchema>

// Re-export MediaItem from media API for backwards compatibility
// (Some components import MediaItem from content instead of media)
export type { MediaItem } from '../media'

// Inbound emails (Mailgun) — the admin content-monitoring "Inbound Emails" tab
export {
  InboundEmailSchema,
  InboundEmailStatusSchema,
  InboundEmailListResponseSchema
} from '../../schemas/entities/inbound-email'
export type { InboundEmail, InboundEmailStatus, InboundEmailListResponse } from '../../schemas/entities/inbound-email'

// The Section ContentTarget variant (U6a of section-canonical cutover) is
// added to the existing entity-level discriminated union — see
// `schemas/entities/content-target.ts`. Consumers continue to import
// `ContentTargetSchema` from `@school-journey/shared-types`; the new
// `section` variant flows through automatically.

/**
 * Attachment for inline content data (POST/PUT /v1/content).
 *
 * Casing matches the backend create endpoint (UPPERCASE). The update
 * endpoint's handler-level normalization also accepts lowercase, but
 * for the canonical wire format we mirror create.
 */
export const ContentAttachmentSchema = z.object({
  type: z.enum(['IMAGE', 'FILE', 'VIDEO']),
  url: z.string().url(),
  isCover: z.boolean().optional(),
  name: z.string().optional(),
  // Media Library `publicId` (a CUID) of the existing photo, carried on
  // update so the backend can reconcile ContentRecordMedia / coverMedia to the
  // submitted set. Not the `Media.id` PK. Optional/additive; absent for
  // legacy clients and non-media attachments.
  mediaId: z.string().optional()
})

export type ContentAttachment = z.infer<typeof ContentAttachmentSchema>

/**
 * An attachment on the UPDATE path, where `url` may be absent.
 *
 * Update is the only direction that re-submits rows the client did not itself
 * upload, and `Media.cloudinaryUrl` is nullable — a photo still deriving, or one
 * whose derive failed, has no url to send. A client that claims
 * `attachmentsIncludeAllMedia` must re-submit that row anyway or the reconcile
 * reads its absence as a removal and deletes it, so the contract has to let a
 * `mediaId` stand alone. The reconcile keys on `mediaId` and never reads `url`.
 *
 * Create keeps {@link ContentAttachmentSchema} unchanged: there is no existing
 * row to name there, so an attachment with no url names nothing.
 *
 * `type` is also re-declared lowercase here — see the field comment.
 *
 * Mirrors `attachmentSchema` in
 * `apps/backend/src/api/content/v1/use-cases/(core)/update-single/schema.ts`.
 */
export const UpdateContentAttachmentSchema = ContentAttachmentSchema.extend({
  /**
   * LOWERCASE, unlike the create schema's `IMAGE | FILE | VIDEO`.
   *
   * Not a style choice — `attachmentSchema` in the update endpoint
   * (`update-single/schema.ts`) is `z.enum(['image','file','video'])`, and the
   * composer sends lowercase. Inheriting the uppercase enum meant any consumer
   * that validated a real PUT body against this schema rejected it. The casing
   * split between the two endpoints is pre-existing; this schema describes the
   * one it belongs to.
   */
  type: z.enum(['image', 'file', 'video']),
  url: z.string().url().optional()
}).refine(attachment => !!attachment.url || !!attachment.mediaId, {
  message: 'An attachment must carry a url or a mediaId'
})

export type UpdateContentAttachment = z.infer<typeof UpdateContentAttachmentSchema>

/**
 * Location payload for content (events, etc.).
 * Mirrors backend create/update `locationSchema`.
 */
export const ContentLocationSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  address: z.string()
})

export type ContentLocation = z.infer<typeof ContentLocationSchema>

/**
 * Inline content body for POST/PUT /v1/content.
 * On create, `body` is required; on update, all fields optional.
 */
export const ContentDataSchema = z.object({
  title: z.string().optional(),
  body: z.string(),
  attachments: z.array(ContentAttachmentSchema).optional()
})

export type ContentData = z.infer<typeof ContentDataSchema>

/** Target types an album post may carry (SCH-2109, KTD5). */
export const ALBUM_POST_TARGET_TYPES = ['group', 'section'] as const

/**
 * Shared zod `superRefine` for an album post (SCH-2109, KTD5): a create payload
 * with `sharedCollectionId` set. A no-op when it is absent, so ordinary posts
 * keep today's behaviour.
 *
 * Rules, each reported on its own path:
 * - an album post is a `POST` (the only content type the feed renders as the
 *   album card);
 * - the album is the whole media payload, so `mediaIds`, `coverMediaId` and
 *   `data.attachments` must be absent (an empty array counts as absent);
 * - every target is a `group` or `section`, and there is at least one. Person,
 *   list, `app_message`, `home_screen` and `app_home` targets reach no Stream
 *   feed the grant can follow, and a home card is not a feed post.
 *
 * Applied by this contract and by the backend create schema so a direct POST
 * cannot bypass it.
 */
export function refineAlbumPostFields(
  fields: {
    sharedCollectionId?: string | null
    contentType?: string | null
    mediaIds?: readonly string[] | null
    coverMediaId?: string | null
    presentationStyle?: string | null
    targets?: ReadonlyArray<{ type: string }> | null
    data?: { attachments?: readonly unknown[] | null } | null
  },
  ctx: z.RefinementCtx
): void {
  if (fields.sharedCollectionId == null) return
  if (fields.contentType !== 'POST') {
    ctx.addIssue({ code: 'custom', path: ['contentType'], message: 'An album post must have contentType POST' })
  }
  const hasMedia =
    (fields.mediaIds?.length ?? 0) > 0 ||
    (fields.coverMediaId != null && fields.coverMediaId !== '') ||
    (fields.data?.attachments?.length ?? 0) > 0
  if (hasMedia) {
    ctx.addIssue({
      code: 'custom',
      path: ['sharedCollectionId'],
      message: 'An album post cannot also carry mediaIds, coverMediaId or attachments'
    })
  }
  if (fields.presentationStyle != null) {
    ctx.addIssue({
      code: 'custom',
      path: ['presentationStyle'],
      message: 'An album post cannot be a home card'
    })
  }
  const targets = fields.targets ?? []
  const allowed: readonly string[] = ALBUM_POST_TARGET_TYPES
  if (targets.length === 0 || targets.some(target => !allowed.includes(target.type))) {
    ctx.addIssue({
      code: 'custom',
      path: ['targets'],
      message: 'An album post must target at least one group or section, and only groups or sections'
    })
  }
}

/** Create statuses that publish the post right away. */
const PUBLISH_NOW_STATUSES: readonly string[] = ['READY_TO_PUBLISH', 'SEND_NOW']

/**
 * Shared zod `superRefine` for a post carrying a poll (SCH-2238): a create
 * payload with `poll` set. A no-op when it is absent, like
 * {@link refineAlbumPostFields}.
 *
 * Rules, each reported on its own path:
 * - a poll post is a `POST` and not a home card;
 * - it is published right away (R23): no draft, no schedule, no `scheduledAt`;
 * - it is not also an album post.
 *
 * The backend create schema must apply it too (feed polls plan U4) so a direct
 * POST cannot bypass it.
 */
export function refinePollPostFields(
  fields: {
    poll?: unknown
    contentType?: string | null
    presentationStyle?: string | null
    status?: string | null
    scheduledAt?: string | null
    sharedCollectionId?: string | null
  },
  ctx: z.RefinementCtx
): void {
  if (fields.poll == null) return
  if (fields.contentType !== 'POST') {
    ctx.addIssue({ code: 'custom', path: ['contentType'], message: 'A poll post must have contentType POST' })
  }
  if (fields.presentationStyle != null) {
    ctx.addIssue({ code: 'custom', path: ['presentationStyle'], message: 'A poll post cannot be a home card' })
  }
  if (fields.status == null || !PUBLISH_NOW_STATUSES.includes(fields.status)) {
    ctx.addIssue({ code: 'custom', path: ['status'], message: 'A poll post must be published right away' })
  }
  if (fields.scheduledAt != null && fields.scheduledAt !== '') {
    ctx.addIssue({ code: 'custom', path: ['scheduledAt'], message: 'A poll post cannot be scheduled' })
  }
  if (fields.sharedCollectionId != null) {
    ctx.addIssue({ code: 'custom', path: ['poll'], message: 'A poll post cannot also be an album post' })
  }
}

/**
 * POST /v1/content
 *
 * Mirrors `apps/backend/src/api/content/v1/use-cases/(core)/create-single/schema.ts#ContentCreateRequestSchema`.
 * Update this schema and the backend in lockstep — they are the
 * canonical wire contract. Deprecated fields (notificationChannels,
 * enableSmsEscalation, notificationOnly, platformAnnouncementId, etc.)
 * are intentionally omitted; new callers must not adopt them.
 */
export const CreateContentRequestSchema = z
  .object({
    contentType: ContentTypeSchema,
    publisherId: z.string().optional(),
    authorId: z.string().optional(),
    targets: z.array(ContentTargetSchema),
    status: z.enum(['DRAFT', 'READY_TO_PUBLISH', 'SCHEDULED', 'SEND_NOW']),
    tags: z.array(z.string()).optional(),
    location: ContentLocationSchema.optional(),
    visibility: z.enum(['PUBLIC', 'FRIENDS', 'GROUP_MEMBERS']).optional(),
    commentsEnabled: z.boolean().optional(),
    reactionsEnabled: z.boolean().optional(),
    notificationMode: NotificationModeSchema.optional(),
    smsFollowUp: z.number().positive().optional(),
    broadcast: z.enum(['SMS', 'EMAIL']).optional(),
    sourceSystem: z.enum(['PLATFORM', 'MOBILE_APP', 'WEB_APP', 'EMAIL', 'API', 'IMPORT', 'CUSTOM']).optional(),
    sourceSystemId: z.string().optional(),
    originalPublishedAt: z.string().optional(),
    mediaIds: z.array(z.string()).optional(),
    coverMediaId: z.string().optional(),
    /**
     * Public id of the one album this post shares (SCH-2109). Set only at create;
     * see {@link refineAlbumPostFields} for what it excludes.
     */
    sharedCollectionId: z.string().min(1).optional(),
    /**
     * The one poll this post carries (SCH-2238). Set only at create; see
     * {@link refinePollPostFields} for what it excludes.
     */
    poll: PollCreateSchema.optional(),
    /**
     * `null` clears a stale schedule (e.g. taking a SCHEDULED card live now);
     * omitting the field leaves the stored value untouched.
     */
    scheduledAt: z.string().nullable().optional(),
    expiresAt: z.string().optional(),
    // In-app message / home-card fields (presentationStyle = HOME_CARD). When set,
    // the backend persists the home-card fields and skips the Stream mirror.
    presentationStyle: z.enum(['HOME_CARD']).optional(),
    pinned: z.boolean().optional(),
    dismissible: z.boolean().optional(),
    data: ContentDataSchema
  })
  .superRefine((data, ctx) => {
    refineAlbumPostFields(data, ctx)
    refinePollPostFields(data, ctx)
  })

export type CreateContentRequest = z.infer<typeof CreateContentRequestSchema>

export const CreateContentResponseSchema = z.object({
  data: ContentRecordSchema,
  message: z.string()
})

export type CreateContentResponse = z.infer<typeof CreateContentResponseSchema>

/** POST /v1/content/:contentId/clone — duplicates a home card into a new DRAFT. */
export const CloneContentResponseSchema = z.object({
  data: z.object({ id: z.string(), publicId: z.string() }),
  message: z.string()
})
export type CloneContentResponse = z.infer<typeof CloneContentResponseSchema>

/**
 * PUT /v1/content/:contentId
 *
 * Mirrors `apps/backend/src/api/content/v1/use-cases/(core)/update-single/schema.ts#ContentUpdateRequestSchema`.
 * Note: backend uses `z.looseObject` so unknown fields are silently
 * dropped server-side; the shared-types schema is strict and lists the
 * full set of supported fields.
 */
export const UpdateContentRequestSchema = z.object({
  data: z
    .object({
      title: z.string().optional(),
      body: z.string().optional(),
      attachments: z.array(UpdateContentAttachmentSchema).optional()
    })
    .optional(),
  status: z.enum(['DRAFT', 'READY_TO_PUBLISH', 'SCHEDULED', 'SEND_NOW', 'ARCHIVED']).optional(),
  targets: z.array(ContentTargetSchema).optional(),
  notificationMode: NotificationModeSchema.optional(),
  description: z.string().optional(),
  /**
   * `null` clears a stale schedule (e.g. taking a SCHEDULED card live now);
   * omitting the field leaves the stored value untouched.
   */
  scheduledAt: z.string().nullable().optional(),
  /**
   * `null` clears the expiration back to "never expires"; omitting the field
   * leaves the stored value untouched.
   *
   * The endpoint (`update-single/schema.ts`) and the service have accepted null
   * since home cards gained an editable expiry; only this mirror lagged, and it
   * type-errored the composer's own `payload.expiresAt = ... : null`.
   */
  expiresAt: z.string().nullable().optional(),
  commentsEnabled: z.boolean().optional(),
  reactionsEnabled: z.boolean().optional(),
  mediaIds: z.array(z.string()).optional(),
  coverMediaId: z.string().optional(),
  /**
   * Rejected outright (SCH-2109, KTD5): an album post is created with its album
   * and never gains, loses or swaps one. Declared so the key is refused rather
   * than silently stripped.
   */
  sharedCollectionId: z.never({ error: 'sharedCollectionId can only be set when a post is created' }).optional(),
  /**
   * Rejected outright (SCH-2238): polls are never added, edited or removed
   * after the post is created. Declared so the key is refused rather than
   * silently stripped.
   */
  poll: z.never({ error: 'poll can only be set when a post is created' }).optional(),
  /**
   * The submitting client enumerates EVERY media row on this record — including
   * the documents and audio that `attachmentsJson` cannot represent — so a row
   * absent from `data.attachments` is a deliberate removal, not a blind spot.
   *
   * WHY THIS IS EXPLICIT RATHER THAN INFERRED: `data.attachments === []` is an
   * overloaded signal. It means both "clear everything" AND "I am a client that
   * cannot see documents, so I have nothing to say about them". Every heuristic
   * that tries to tell those apart from the payload alone fails one of the two
   * directions — reading it as a clear silently wipes a PDF the author never
   * saw; reading it as a blind spot makes removing the LAST document impossible.
   * Only the client knows which it is, so it says so.
   *
   * Omit it (or send `false`) and the backend falls back to its conservative
   * heuristic, which never deletes a document the submitter did not name. That
   * is the version-skew-safe default: clients that predate this field keep their
   * existing, protective behaviour.
   */
  attachmentsIncludeAllMedia: z.boolean().optional(),
  /**
   * The `mediaSetVersion` the client was handed when it READ the media set it is
   * now claiming to enumerate. Required in practice by
   * `attachmentsIncludeAllMedia`: without a matching token the backend refuses
   * the claim (see below).
   *
   * Optimistic concurrency, deliberately soft. Losing an un-applied removal is
   * recoverable; deleting a colleague's upload is not.
   *
   * A mismatch means the record's media changed after this client last looked,
   * so its silence about a row may be ignorance rather than intent.
   *
   * WHAT A REFUSAL DOES: the reconcile is SKIPPED ENTIRELY — no removal of any
   * kind lands, photos included. It is not a downgrade to a non-authoritative
   * reconcile: the flag does not authorise deleting a photo in the first place
   * (the id-aware gate does, and photos are always in scope), so merely dropping
   * it would still delete the row the refusal exists to protect. The rest of the
   * save lands, and `mediaIds` in the same payload still appends.
   *
   * The response then carries `mediaRemovalsSkipped: true` so the client can
   * tell the author their removal did not take effect.
   *
   * Opaque — produced by `computeMediaSetVersion` on the backend, compared for
   * equality, never parsed.
   */
  attachmentsVersion: z.string().optional(),
  /**
   * Home-card behaviour (`presentationStyle = HOME_CARD`): whether a reader can
   * clear the card. Omitted → left unchanged, and it is written only for a card
   * that IS a home card.
   *
   * There is deliberately no `pinned` here. Nothing sends it — pin order is
   * owned by the card list's own reorder endpoint — and an update field with no
   * sender is a field waiting to un-pin a card the first time some client
   * echoes back a payload it was handed.
   */
  dismissible: z.boolean().optional(),
  /**
   * The web address a link card (`presentationStyle = HOME_CARD`,
   * `cardKind = 'link'`) opens. Link cards only: the backend refuses it on any
   * other record. Omitted → the URL is left unchanged. Not nullable, because a
   * link card always needs a URL.
   */
  linkUrl: z
    .string()
    .trim()
    .refine(isHttpUrl, { error: 'linkUrl must be an http:// or https:// web address' })
    .optional()
})

export type UpdateContentRequest = z.infer<typeof UpdateContentRequestSchema>

export const UpdateContentResponseSchema = z.object({
  data: ContentRecordSchema.extend({
    /**
     * The update claimed to enumerate every media row (`attachmentsIncludeAllMedia`)
     * but its `attachmentsVersion` was stale or absent, so the save landed
     * WITHOUT its media removals.
     *
     * Present only when the refusal actually declined a media CHANGE: a row the
     * record holds was unnamed, the payload named a different cover, or (on the
     * legacy blob lane) the submitted set differs from the stored one. A
     * title-only edit against a changed card does not set it.
     *
     * Note the flag says a change was declined, not specifically that the AUTHOR
     * removed something — a colleague adding a row also leaves the author's
     * full-set claim unverifiable. Client copy should be true in both shapes.
     * The edit has to be repeated against a fresh read either way.
     */
    mediaRemovalsSkipped: z.boolean().optional()
  }),
  message: z.string()
})

export type UpdateContentResponse = z.infer<typeof UpdateContentResponseSchema>

/**
 * GET /v1/schools/:schoolId/platform-content/mgmt-announcement-categories
 */
export const GetAnnouncementCategoriesResponseSchema = z.object({
  data: z.array(ContentCategorySchema),
  message: z.string()
})

export type GetAnnouncementCategoriesResponse = z.infer<typeof GetAnnouncementCategoriesResponseSchema>

/**
 * GET /v1/schools/:schoolId/platform-content/mgmt-news-categories
 */
export const GetNewsCategoriesResponseSchema = z.object({
  data: z.array(ContentCategorySchema),
  message: z.string()
})

export type GetNewsCategoriesResponse = z.infer<typeof GetNewsCategoriesResponseSchema>

/**
 * GET /v1/schools/:schoolId/content/platform-categories
 */
export const GetPlatformCategoriesResponseSchema = z.object({
  data: z.object({
    announcementCategories: z.array(ContentCategorySchema),
    newsCategories: z.array(ContentCategorySchema),
    eventCategories: z.array(ContentCategorySchema).optional()
  }),
  message: z.string()
})

export type GetPlatformCategoriesResponse = z.infer<typeof GetPlatformCategoriesResponseSchema>

// Resend a published record's push notification
export * from './resend-notifications'
